import React, { createContext, useContext, useState, useCallback } from 'react';

const UndoRedoContext = createContext();

export const useUndoRedo = () => {
    const context = useContext(UndoRedoContext);
    if (!context) {
        throw new Error('useUndoRedo must be used within UndoRedoProvider');
    }
    return context;
};

export const UndoRedoProvider = ({ children }) => {
    // Load from session storage on mount
    const loadHistory = () => {
        try {
            const saved = sessionStorage.getItem('status_change_history');
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            return [];
        }
    };

    const [undoStack, setUndoStack] = useState(loadHistory());
    const [redoStack, setRedoStack] = useState([]);

    // Save to session storage whenever history changes
    const saveHistory = (history) => {
        try {
            sessionStorage.setItem('status_change_history', JSON.stringify(history));
        } catch (e) {
            console.error('Failed to save undo history:', e);
        }
    };

    const addChange = useCallback((change) => {
        const newHistory = [...undoStack, change];
        setUndoStack(newHistory);
        setRedoStack([]); // Clear redo stack when new change is made
        saveHistory(newHistory);
    }, [undoStack]);

    const undo = useCallback(() => {
        if (undoStack.length === 0) return null;

        const lastChange = undoStack[undoStack.length - 1];
        const newUndoStack = undoStack.slice(0, -1);
        const newRedoStack = [...redoStack, lastChange];

        setUndoStack(newUndoStack);
        setRedoStack(newRedoStack);
        saveHistory(newUndoStack);

        return lastChange;
    }, [undoStack, redoStack]);

    const redo = useCallback(() => {
        if (redoStack.length === 0) return null;

        const lastRedo = redoStack[redoStack.length - 1];
        const newRedoStack = redoStack.slice(0, -1);
        const newUndoStack = [...undoStack, lastRedo];

        setUndoStack(newUndoStack);
        setRedoStack(newRedoStack);
        saveHistory(newUndoStack);

        return lastRedo;
    }, [undoStack, redoStack]);

    const canUndo = undoStack.length > 0;
    const canRedo = redoStack.length > 0;

    const value = {
        addChange,
        undo,
        redo,
        canUndo,
        canRedo,
        undoStack,
        redoStack
    };

    return (
        <UndoRedoContext.Provider value={value}>
            {children}
        </UndoRedoContext.Provider>
    );
};
